import { cn } from "../lib/cn";

export interface ChipOption {
  value: string;
  label: string;
}

/**
 * Single-select chip group — the MRP domain/category/priority pickers from the imported
 * design. A row of mutually-exclusive buttons rather than a `<select>`: all options are
 * visible at once, which matters for a pharmacist scanning the MRP taxonomy during a live
 * patient encounter.
 */
export function ChipSelect({
  options,
  value,
  onChange,
  equalWidth,
  className,
}: {
  options: ChipOption[];
  value: string;
  onChange: (value: string) => void;
  /** Priority-style chips (few, wide) size equally; category chips wrap and hug content. */
  equalWidth?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)} role="radiogroup">
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              "min-h-10 rounded border px-3.5 text-sm font-semibold transition-colors",
              equalWidth && "flex-1",
              selected ? "border-clinical-high bg-clinical-high text-background" : "border-charcoal-900/20 bg-transparent text-charcoal-800/70 hover:bg-charcoal-900/5",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
